import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import {
  DatePicker,
  DateView,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import en from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import pt from "date-fns/locale/pt-BR";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  resolveMonthlyPeriodRange,
  type TReportMonthPeriodType,
} from "./reportExecutionUtils";

interface IReportMonthPeriodFilterProps {
  createdError?: string;
  createdStart: Date | null;
  selectedPeriod: TReportMonthPeriodType;
  onPeriodChange: (value: TReportMonthPeriodType) => void;
  onSelectedMonthChange: (date: Date | null) => void;
}

const REPORT_EXECUTION_TRANSLATION_PATH = "page.reports.execution.";

const monthViews: DateView[] = ["month", "year"];

const isSameDateTime = (left: Date | null, right: Date | null) =>
  left?.getTime() === right?.getTime();

const ReportMonthPeriodFilter: React.FC<IReportMonthPeriodFilterProps> = ({
  createdError,
  createdStart,
  selectedPeriod,
  onPeriodChange,
  onSelectedMonthChange,
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const [monthDate, setMonthDate] = useState<Date | null>(
    createdStart ?? new Date(),
  );

  const adapterLocale = useMemo(() => {
    switch (language) {
      case "en-US":
        return en;
      case "es":
        return es;
      default:
        return pt;
    }
  }, [language]);

  const handlePeriodChange = useCallback(
    (event: SelectChangeEvent<TReportMonthPeriodType>) => {
      const nextPeriod = event.target.value as TReportMonthPeriodType;

      onPeriodChange(nextPeriod);

      if (nextPeriod === "this_month") {
        setMonthDate(
          resolveMonthlyPeriodRange({ monthPeriodType: nextPeriod }).start,
        );
        return;
      }

      if (nextPeriod === "last_month") {
        setMonthDate(
          resolveMonthlyPeriodRange({ monthPeriodType: nextPeriod }).start,
        );
      }
    },
    [onPeriodChange],
  );

  const handleMonthChange = useCallback(
    (date: Date | null) => {
      setMonthDate((prev) => (isSameDateTime(prev, date) ? prev : date));
      onSelectedMonthChange(date);
    },
    [onSelectedMonthChange],
  );

  useEffect(() => {
    if (!createdStart) {
      return;
    }

    setMonthDate((prev) =>
      isSameDateTime(prev, createdStart) ? prev : createdStart,
    );
  }, [createdStart]);

  return (
    <>
      <Grid item xs={12} md={4} lg={3}>
        <FormControl size="small" fullWidth error={!!createdError}>
          <InputLabel id="report-created-period-label">
            {t(REPORT_EXECUTION_TRANSLATION_PATH + "created")}
          </InputLabel>
          <Select
            labelId="report-created-period-label"
            value={selectedPeriod}
            label={t(REPORT_EXECUTION_TRANSLATION_PATH + "created")}
            onChange={handlePeriodChange}
          >
            <MenuItem value="this_month">
              {t(REPORT_EXECUTION_TRANSLATION_PATH + "period.thisMonth")}
            </MenuItem>
            <MenuItem value="last_month">
              {t(REPORT_EXECUTION_TRANSLATION_PATH + "period.lastMonth")}
            </MenuItem>
            <MenuItem value="select_month">
              {t(REPORT_EXECUTION_TRANSLATION_PATH + "period.selectMonth")}
            </MenuItem>
          </Select>
          {createdError && <FormHelperText>{createdError}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={2}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={adapterLocale}
        >
          <DatePicker
            disableFuture
            disabled={selectedPeriod !== "select_month"}
            label={t(REPORT_EXECUTION_TRANSLATION_PATH + "monthYear")}
            value={monthDate}
            views={monthViews}
            onChange={handleMonthChange}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: !!createdError,
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
    </>
  );
};

export default memo(ReportMonthPeriodFilter);
